import {
  DndContext,
  DragEndEvent,
  useSensor,
  useSensors,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { RadioOption, SurveyQuestion } from '@employ-engage/data';
import cloneDeep from 'lodash.clonedeep';
import { nanoid } from 'nanoid';
import Toggle from '../toggle/toggle';
import { useTranslation } from 'react-i18next';
import { QuestionOptionsItems } from './question-options-items';

export interface QuestionOptionsFormProps {
  values: any;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void;
  pageIndex: number;
  questionIndex: number;
  question: any;
  errors: any;
  touched: any;
}

export function QuestionOptionsForm({
  values,
  setFieldValue,
  pageIndex,
  questionIndex,
  question,
  errors,
  touched,
}: QuestionOptionsFormProps) {
  const { t } = useTranslation(['common']);
  const addOption = (pageIndex: number, questionIndex: number) => {
    const newPages = cloneDeep(values.pages);
    newPages[pageIndex].questions[questionIndex].options?.push({
      id: nanoid(),
      label: '',
    });
    setFieldValue('pages', newPages);
  };

  const setIsColumn = (
    isColumn: boolean,
    pageIndex: number,
    questionIndex: number
  ) => {
    const newPages = cloneDeep(values.pages);
    (newPages[pageIndex].questions[questionIndex] as SurveyQuestion).isColumn =
      isColumn;
    setFieldValue('pages', newPages);
  };

  const handleDragEnd = (
    event: DragEndEvent,
    pageIndex: number,
    questionIndex: number
  ) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const newPages = cloneDeep(values.pages);
      const oldIndex = active.data.current?.sortable?.index;
      const newIndex = over.data.current?.sortable?.index;

      if (oldIndex !== undefined && newIndex !== undefined) {
        const [removed] =
          newPages[pageIndex].questions[questionIndex].options?.splice(
            oldIndex,
            1
          ) || [];
        newPages[pageIndex].questions[questionIndex].options?.splice(
          newIndex,
          0,
          removed
        );
        setFieldValue('pages', newPages);
      }
    }
  };

  const sensors = useSensors(
    useSensor(TouchSensor),
    useSensor(MouseSensor),
    useSensor(KeyboardSensor)
  );

  return (
    <>
      <div className="mx-4 space-y-4 my-4">
        <DndContext
          onDragEnd={(event: any) =>
            handleDragEnd(event, pageIndex, questionIndex)
          }
          sensors={sensors}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={question?.options || []}
            strategy={verticalListSortingStrategy}
          >
            <div>
              {question?.options?.map(
                (option: RadioOption, optionIndex: number) => {
                  return (
                    <QuestionOptionsItems
                      key={option.id}
                      optionId={option.id}
                      optionIndex={optionIndex}
                      setFieldValue={setFieldValue}
                      pageIndex={pageIndex}
                      questionIndex={questionIndex}
                      question={question}
                      errors={errors}
                      touched={touched}
                      values={values}
                    />
                  );
                }
              )}
            </div>
          </SortableContext>
        </DndContext>
        <button
          type="button"
          onClick={() => addOption(pageIndex, questionIndex)}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-500 hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {t('Add Option')}
        </button>
      </div>
      <Toggle
        id={`pages[${pageIndex}].questions[${questionIndex}].isColumn`}
        label={t('Make this a column? (default is row)')}
        enabled={question?.isColumn || false}
        setEnabled={(isColumn) =>
          setIsColumn(isColumn, pageIndex, questionIndex)
        }
      />
    </>
  );
}

export default QuestionOptionsForm;
